/**
 * chem_utils.js
 * Utility functions for working with chemical structures, mostly predicates on atoms and bonds.
 * The principle is to have a file that doesn't import any of our business objects.
 * All the knowledge of the business object structure is in the "internal functions" at the bottom.
 */
/**
 * @typedef {import('BMapsModel').Atom} Atom
 */
import { getElementWeight } from '../chemistry/ElementInfo';

export function isHydrogen(atom) {
    return getElement(atom) === 'H';
}

export function isNotHydrogen(atom) {
    return !isHydrogen(atom);
}

export function isCarbon(atom) {
    return getElement(atom) === 'C';
}

export function sameElement(atom1, atom2) {
    return getElement(atom1) === getElement(atom2);
}

export function isPhosphorus(atom) { return getElement(atom) === 'P'; }
export function isOxygen(atom) { return getElement(atom) === 'O'; }
export function isSulfur(atom) { return getElement(atom) === 'S'; }
export function isHalogen(atom) {
    return ['Cl', 'F', 'Br', 'I', 'At', 'Ts'].includes(getElement(atom));
}

export function getNHalogens(atom) {
    return getPartnerAtoms(atom).filter(isHalogen).length;
}

/**
 *
 * @param {Atom[]} atoms
 * @param {Boolean?} includeHs
 * @returns
 */
export function filterHydrogens(atoms, includeHs) {
    const keep = !!includeHs;
    return atoms.filter((atom) => keep === isHydrogen(atom));
}

export function removeHydrogens(atoms) { return filterHydrogens(atoms, false); }
export function onlyHydrogens(atoms) { return filterHydrogens(atoms, true); }

export function countHeavyAtoms(atoms) { return removeHydrogens(atoms).length; }
export function calculateMolecularWeight(atoms) {
    return atoms.reduce((mw, atom) => mw + getElementWeight(getElement(atom)), 0);
}

const POLAR_HYDROGEN_PARTNERS = ['N', 'O', 'F', 'S', 'Se'];

export function isPolarHydrogen(atom) {
    if (isHydrogen(atom)) {
        const partner = getPartnerAtoms(atom)[0];
        if (partner) {
            return POLAR_HYDROGEN_PARTNERS.includes(getElement(partner));
        }
    }
    return false;
}

export function isNonPolarHydrogen(atom) {
    return isHydrogen(atom) && !isPolarHydrogen(atom);
}

/**
 * True when the atom has three rotationally-equivalent terminal substituents
 * (XH3 or X(Hal)3 on a tetrahedral / quaternized / adduct center). Used to
 * dedupe results from substitutions that differ only in which equivalent
 * terminal was chosen.
 * @param {Atom} atom
 * @returns {boolean}
 */
export function atomHasEquivalentTerminals(atom) {
    const GROUP_14_ELEMENTS = ['C', 'Si', 'Ge', 'Sn', 'Pb'];
    const GROUP_15_ELEMENTS = ['N', 'P', 'As', 'Sb', 'Bi'];
    const elem = getElement(atom);
    const isEligibleElement = elem === 'B'
        || GROUP_14_ELEMENTS.includes(elem)
        || GROUP_15_ELEMENTS.includes(elem);
    if (!isEligibleElement) return false;
    const neighbors = getPartnerAtoms(atom);
    if (neighbors.length !== 4) return false;
    if (onlyHydrogens(neighbors).length === 3) return true;
    return getNHalogens(atom) === 3;
}

/**
 * True when any bond on the atom is aromatic / delocalized / conjugated.
 * Distinguishes pyridine/pyrazole/triazole/indazole N (aromatic) from amide,
 * sulfonamide, sp3 amine, anilinic NH2 (non-aromatic). Nitro N has delocalized
 * bonds and registers as aromatic — a known harmless false positive for the
 * N-N filter that uses this predicate (nitro N doesn't generate N-N candidates).
 * @param {Atom} atom
 * @returns {boolean}
 */
export function isAromaticAtom(atom) {
    return getPartnerBonds(atom).some((bond) => (
        isAromaticBond(bond) || isDelocalizedBond(bond) || isConjugatedBond(bond)
    ));
}

export function isAromaticBond(bond) {
    return isAromaticBondOrder(getFunctionalBondOrder(bond));
}

export function isDelocalizedBond(bond) {
    return isDelocalizedBondOrder(getFunctionalBondOrder(bond));
}

export function isConjugatedBond(bond) {
    return isConjugatedBondOrder(getFunctionalBondOrder(bond));
}

// Internal but exported
// These functions are aware of implementation specific details,
// but are also exported.
export function isAromaticBondOrder(bondOrder) {
    return bondOrder === 8 || bondOrder === 9;
}

export function isDelocalizedBondOrder(bondOrder) {
    return bondOrder === 7;
}

export function isConjugatedBondOrder(bondOrder) {
    return bondOrder === 6;
}

// Internal only functions
// These functions are aware of implementation specific details and are not exported.
function getElement(atom) { return atom.elem; }
function getPartnerAtoms(atom) { return [...atom.bondedAtoms]; }
function getPartnerBonds(atom) { return [...atom.bonds]; }
function getFunctionalBondOrder(bond) { return bond.orderOriginal; }
